import { runAvailityVob } from './availity-service';
import { fetchPayorList, mapPayorsToWebformPayors } from './configuration-service';
import { resolveAuthToken } from './guest-auth';
import {
  buildPatientPayload,
  createPatient,
  savePatientInsurance,
} from './patient-service';
import type {
  VobWebformConfig,
  VobWebformResult,
  VobWebformRuntimeConfig,
  VobWebformSubmitPayload,
  VobWebformThemeAttributes,
} from './types';
import { buildVobFromAvaility } from './build-vob-from-availity';
import { sendVobWebformEmail } from './email-service';
import { validateRequiredFormFields } from './form-validation';
import { buildLeadFromForm, createLead } from './lead-service';
import { saveVob } from './vob-service';

export type { VobWebformRuntimeConfig } from './types';

export function resolveApiBase(instanceAttr: string | null): string {
  const fromAttr = (instanceAttr ?? '').trim();
  if (fromAttr) {
    return fromAttr.replace(/\/$/, '');
  }
  const fromBuild =
    (typeof __FINPAY_VOB_WEBFORM_API_BASE__ !== 'undefined'
      ? __FINPAY_VOB_WEBFORM_API_BASE__
      : '') ||
    (process.env.REACT_APP_UNIFIED_AWS_APPGATEWAY_URI ?? '');
  if (!fromBuild) {
    throw new Error('Missing instance attribute or build-time API base');
  }
  return fromBuild.replace(/\/$/, '');
}

export async function loadWebformConfig(
  runtime: VobWebformRuntimeConfig
): Promise<VobWebformConfig> {
  const authToken = await resolveAuthToken(runtime.apiBase, runtime.authToken);
  const payors = await fetchPayorList(runtime.apiBase, authToken);

  return {
    payors: mapPayorsToWebformPayors(payors),
    clientId: runtime.clientId,
    facilityId: runtime.facilityId,
    redirectOnSuccessUrl: runtime.redirectOnSuccessUrl,
    ...runtime.theme,
  };
}

export async function submitVobWebform(
  runtime: VobWebformRuntimeConfig,
  form: VobWebformSubmitPayload
): Promise<VobWebformResult> {
  if (runtime.queue !== false) {
    return submitQueuedLead(runtime, form);
  }

  try {
    const authToken = await resolveAuthToken(runtime.apiBase, runtime.authToken);
    const payorId = Number(form.payor_id);
    const payorPlanId = Number(form.payor_plan_id);

    if (!payorId || Number.isNaN(payorId)) {
      return {
        success: false,
        validation: true,
        message: 'Please select an insurance company',
      };
    }

    const patientPayload = buildPatientPayload(form, runtime.clientId);
    const patient = await createPatient(
      runtime.apiBase,
      authToken,
      patientPayload
    );

    const subscriberName = `${patientPayload.contact.firstName} ${patientPayload.contact.lastName}`.trim();

    await savePatientInsurance(runtime.apiBase, authToken, patient.patientId, {
      payorId,
      payorPlanId: payorPlanId > 0 ? payorPlanId : 0,
      memberId: form.member_id?.trim() ?? '',
      groupNumber: '',
      subscriberName,
      subscriberRelation: 1,
    });

    const availityVob = await runAvailityVob(
      runtime.apiBase,
      authToken,
      patient.patientId,
      runtime.facilityId
    );

    if (availityVob.activePolicy === false) {
      return {
        success: false,
        validation: false,
        message: "Patient's insurance policy is not active",
      };
    }

    const vobPayload = buildVobFromAvaility(availityVob, {
      patientId: patient.patientId,
      clientId: runtime.clientId,
      facilityId: runtime.facilityId,
      payorId,
      payorPlanId: payorPlanId > 0 ? payorPlanId : 0,
      memberId: form.member_id,
      webformNotes: form.webform_notes,
    });

    let savedVobId: number | undefined;
    let vobSaveError: Error | undefined;
    try {
      const savedVob = await saveVob(runtime.apiBase, authToken, vobPayload);
      if (savedVob.vobId != null && savedVob.vobId > 0) {
        savedVobId = savedVob.vobId;
      }
    } catch (error) {
      vobSaveError =
        error instanceof Error ? error : new Error('Failed to save VOB');
    }

    const sendTo = runtime.sendTo?.trim();
    if (sendTo) {
      try {
        await sendVobWebformEmail(
          runtime.apiBase,
          authToken,
          sendTo,
          form,
          patient.patientId,
          savedVobId,
          runtime.emailTemplateId
        );
      } catch (emailError) {
        console.warn(
          'VOB webform notification email failed:',
          emailError instanceof Error ? emailError.message : emailError
        );
      }
    }

    if (vobSaveError) {
      throw vobSaveError;
    }

    return {
      success: true,
      validation: false,
      message: 'Insurance verification submitted successfully',
    };
  } catch (error) {
    return {
      success: false,
      validation: false,
      message:
        error instanceof Error ? error.message : 'Unable to verify insurance',
    };
  }
}

export async function submitQueuedLead(
  runtime: VobWebformRuntimeConfig,
  form: VobWebformSubmitPayload
): Promise<VobWebformResult> {
  const validationError = validateRequiredFormFields(form);
  if (validationError) {
    return validationError;
  }

  try {
    const authToken = await resolveAuthToken(runtime.apiBase, runtime.authToken);
    const lead = buildLeadFromForm(form, runtime);
    if (!lead.apiToken) {
      lead.apiToken = authToken;
    }
    await createLead(runtime.apiBase, authToken, lead);

    return {
      success: true,
      validation: false,
      message: 'Your information has been submitted successfully',
    };
  } catch (error) {
    return {
      success: false,
      validation: false,
      message:
        error instanceof Error ? error.message : 'Unable to submit your information',
    };
  }
}

export function collectUtmParams(): Record<string, string> {
  const params: Record<string, string> = {};
  const search = new URLSearchParams(window.location.search);
  search.forEach((value, key) => {
    if (key.startsWith('utm_')) {
      params[key] = value;
    }
  });
  return params;
}
